import { Clock, Plus, Trash2, Loader2, Search } from 'lucide-react'
import { useState, useCallback, useMemo } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useDefaultTimelogHours } from '@/hooks/common'
import {
  useMyTasks,
  useCreateTimelog,
  useDeleteTimelog,
  type TimelogWithIssue,
} from '@/hooks/pm'
import { cn } from '@/lib/utils'
import { useEfficiencyStore } from '@/stores/efficiency.store'

/**
 * 快速补录工时组件
 * Requirements: 9.3 - 支持从日历视图快速补录历史工时
 */

interface TimelogQuickEntryProps {
  /** 补录日期 (YYYY-MM-DD) */
  date: string
  /** 该日期已有的工时记录 */
  existingTimelogs?: TimelogWithIssue[]
  /** 成功回调 */
  onSuccess?: () => void
  /** 取消回调 */
  onCancel?: () => void
}

interface NewTimelogEntry {
  issueId: string
  hours: number
  description: string
}

export function TimelogQuickEntry({
  date,
  existingTimelogs = [],
  onSuccess,
  onCancel,
}: TimelogQuickEntryProps) {
  const defaultHours = useDefaultTimelogHours()
  const { preferences, setPreferences } = useEfficiencyStore()
  const lastUsedHours = preferences?.defaultTimelogHours ?? defaultHours

  // 获取我的任务列表用于选择
  const { data: myTasksData } = useMyTasks({
    status: ['in_progress', 'code_review', 'testing'],
    pageSize: 100,
  })
  const myTasks = useMemo(() => myTasksData?.data ?? [], [myTasksData])

  const [searchQuery, setSearchQuery] = useState('')
  const [newEntries, setNewEntries] = useState<NewTimelogEntry[]>([
    { issueId: '', hours: lastUsedHours, description: '' },
  ])
  const [errors, setErrors] = useState<Record<number, string>>({})

  const createMutation = useCreateTimelog()
  const deleteMutation = useDeleteTimelog()

  // 过滤任务列表
  const filteredTasks = useMemo(() => {
    if (!searchQuery) {return myTasks}
    const query = searchQuery.toLowerCase()
    return myTasks.filter(
      (task) =>
        task.code?.toLowerCase().includes(query) ||
        task.title.toLowerCase().includes(query)
    )
  }, [myTasks, searchQuery])

  // 计算已有工时总计
  const existingTotal = useMemo(() => {
    return existingTimelogs.reduce((sum, t) => sum + t.minutes / 60, 0)
  }, [existingTimelogs])

  // 计算新增工时总计
  const newTotal = useMemo(() => {
    return newEntries.reduce((sum, e) => sum + (e.hours || 0), 0)
  }, [newEntries])

  const addEntry = useCallback(() => {
    setNewEntries((prev) => [
      ...prev,
      { issueId: '', hours: lastUsedHours, description: '' },
    ])
  }, [lastUsedHours])

  const removeEntry = useCallback((index: number) => {
    setNewEntries((prev) => prev.filter((_, i) => i !== index))
    setErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[index]
      return newErrors
    })
  }, [])

  const updateEntry = useCallback(
    (index: number, field: keyof NewTimelogEntry, value: string | number) => {
      setNewEntries((prev) =>
        prev.map((entry, i) =>
          i === index ? { ...entry, [field]: value } : entry
        )
      )
      // 清除该条目的错误
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[index]
        return newErrors
      })
    },
    []
  )

  const validateEntries = useCallback(() => {
    const newErrors: Record<number, string> = {}
    let isValid = true

    newEntries.forEach((entry, index) => {
      if (!entry.issueId) {
        newErrors[index] = '请选择任务'
        isValid = false
      } else if (entry.hours <= 0) {
        newErrors[index] = '工时必须大于0'
        isValid = false
      } else if (entry.hours > 24) {
        newErrors[index] = '工时不能超过24小时'
        isValid = false
      }
    })

    // 检查总工时
    if (existingTotal + newTotal > 24) {
      newErrors[-1] = '当日总工时不能超过24小时'
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }, [newEntries, existingTotal, newTotal])

  const handleSubmit = useCallback(async () => {
    if (!validateEntries()) {return}

    // 过滤掉没有选择任务的条目
    const validEntries = newEntries.filter((e) => e.issueId)
    if (validEntries.length === 0) {
      onCancel?.()
      return
    }

    try {
      // 依次创建工时记录
      for (const entry of validEntries) {
        await createMutation.mutateAsync({
          issueId: entry.issueId,
          logDate: date,
          minutes: Math.round(entry.hours * 60),
          description: entry.description || undefined,
        })
      }

      // 记住最后使用的工时数
      const lastEntry = validEntries[validEntries.length - 1]
      if (preferences && lastEntry) {
        setPreferences({ ...preferences, defaultTimelogHours: lastEntry.hours })
      }

      onSuccess?.()
    } catch {
      // 错误由 mutation 处理
    }
  }, [
    validateEntries,
    newEntries,
    date,
    createMutation,
    preferences,
    setPreferences,
    onSuccess,
    onCancel,
  ])

  const handleDeleteExisting = useCallback(
    async (id: string) => {
      try {
        await deleteMutation.mutateAsync(id)
      } catch {
        // 错误由 mutation 处理
      }
    },
    [deleteMutation]
  )

  const isPending = createMutation.isPending || deleteMutation.isPending

  return (
    <div className="space-y-4">
      {/* 已有工时记录 */}
      {existingTimelogs.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground">
            已记录工时 ({existingTotal.toFixed(1)}h)
          </div>
          <div className="space-y-2 rounded-md border p-3">
            {existingTimelogs.map((timelog) => (
              <div
                key={timelog.id}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="font-mono text-xs text-muted-foreground">
                    {timelog.issue?.code}
                  </span>
                  <span className="truncate">{timelog.issue?.title}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="font-medium">
                    {(timelog.minutes / 60).toFixed(1)}h
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => void handleDeleteExisting(timelog.id)}
                    disabled={isPending}
                  >
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 新增工时记录 */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">
            {existingTimelogs.length > 0 ? '补录工时' : '记录工时'}
          </div>
          {newTotal > 0 && (
            <div className="text-sm text-muted-foreground">
              新增: {newTotal.toFixed(1)}h
            </div>
          )}
        </div>

        {/* 任务搜索 */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="搜索任务..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* 工时条目列表 */}
        <div className="space-y-3">
          {newEntries.map((entry, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-start gap-2">
                {/* 任务选择 */}
                <div className="flex-1">
                  <Select
                    value={entry.issueId}
                    onValueChange={(value) => updateEntry(index, 'issueId', value)}
                  >
                    <SelectTrigger
                      className={cn(errors[index] && 'border-destructive')}
                    >
                      <SelectValue placeholder="选择任务" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredTasks.length === 0 ? (
                        <div className="py-2 text-center text-sm text-muted-foreground">
                          {searchQuery ? '未找到匹配的任务' : '暂无进行中的任务'}
                        </div>
                      ) : (
                        filteredTasks.map((task) => (
                          <SelectItem key={task.id} value={task.id}>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-muted-foreground">
                                {task.code}
                              </span>
                              <span className="truncate">{task.title}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* 工时输入 */}
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0.5"
                    max="24"
                    step="0.5"
                    value={entry.hours}
                    onChange={(e) =>
                      updateEntry(index, 'hours', parseFloat(e.target.value) || 0)
                    }
                    className={cn(
                      'w-20 text-center',
                      errors[index] && 'border-destructive'
                    )}
                  />
                  <span className="text-sm text-muted-foreground">h</span>
                </div>

                {/* 删除按钮 */}
                {newEntries.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 flex-shrink-0"
                    onClick={() => removeEntry(index)}
                    disabled={isPending}
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                )}
              </div>

              {/* 描述输入 */}
              <Input
                placeholder="工作描述（可选）"
                value={entry.description}
                onChange={(e) => updateEntry(index, 'description', e.target.value)}
                maxLength={200}
              />

              {/* 错误提示 */}
              {errors[index] && (
                <p className="text-xs text-destructive">{errors[index]}</p>
              )}
            </div>
          ))}
        </div>

        {/* 添加更多按钮 */}
        <Button
          variant="outline"
          size="sm"
          onClick={addEntry}
          disabled={isPending}
          className="w-full"
        >
          <Plus className="mr-1 h-4 w-4" />
          添加更多
        </Button>
      </div>

      {/* 总计和错误 */}
      {errors[-1] && (
        <p className="text-sm text-destructive">{errors[-1]}</p>
      )}

      {(createMutation.error || deleteMutation.error) && (
        <p className="text-sm text-destructive">
          {createMutation.error?.message || deleteMutation.error?.message}
        </p>
      )}

      <div className="flex items-center justify-between border-t pt-4">
        <div className="text-sm">
          <span className="text-muted-foreground">当日总计: </span>
          <span className="font-medium">
            {(existingTotal + newTotal).toFixed(1)}h
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            取消
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                保存中...
              </>
            ) : (
              '保存'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
